import { useCallback, useEffect, useRef, useState } from 'react';

import { APIAdsServe } from '../../../api_ads_public';
import { useAds } from './AdsContext';


/**
 * Загрузка объявления для площадки.
 *
 * Дедупликация по ключу «площадка + сессия»: на экране может оказаться
 * два блока одной площадки, и запрашивать их по отдельности нет смысла —
 * это лишний запрос и лишний расход частотного лимита кампании.
 */
export default function useAdSlot(placement, { enabled = true, count = 1, deps = [] } = {}) {
    const { token, sessionId, disabled, inflight } = useAds();
    const [ads, setAds] = useState([]);
    const [placementInfo, setPlacementInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [revision, setRevision] = useState(0);
    const sequence = useRef(0);
    const active = Boolean(placement && enabled && !disabled && token);
    const depsKey = JSON.stringify(deps);

    useEffect(() => {
        const requestId = ++sequence.current;
        let timer;
        let cancelled = false;
        setAds([]);
        setPlacementInfo(null);
        setLoading(false);
        if (!active) return undefined;
        const load = async () => {
            setLoading(true);
            const key = JSON.stringify([placement, sessionId, count, token]);
            let promise = inflight?.get(key);
            if (!promise) {
                promise = APIAdsServe(token, { placement, count, sessionId });
                inflight?.set(key, promise);
                const cleanup = () => {
                    if (inflight?.get(key) === promise) inflight.delete(key);
                };
                promise.then(cleanup, cleanup);
            }
            let res;
            try { res = await promise; } catch (_) { res = { ads: [] }; }
            if (cancelled || requestId !== sequence.current) return;
            const now = Date.now();
            const items = (res?.ads || []).filter(ad =>
                !ad.expires_at || Number(ad.expires_at) * 1000 > now + 1000);
            setAds(items);
            setPlacementInfo(res?.placement || null);
            setLoading(false);
            const expiry = items[0]?.expires_at;
            const delay = expiry ? Math.max(1000, Number(expiry) * 1000 - now - 1000) : 60000;
            // Пустая выдача тоже обновится: баланс/модерация могли измениться.
            timer = setTimeout(() => setRevision(value => value + 1), delay);
        };
        load();
        return () => { cancelled = true; clearTimeout(timer); };
    }, [active, placement, sessionId, count, token, inflight, depsKey, revision]);

    const refresh = useCallback(() => setRevision(value => value + 1), []);
    const clear = useCallback(() => setAds([]), []);
    const visibleAds = active ? ads : [];
    return { ad: visibleAds[0] || null, ads: visibleAds, placementInfo, loading, refresh, clear };
}